import React from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import { isEmpty, uniqBy } from 'lodash';
import * as Yup from 'yup';

import InstructionsMarkdownEditorFormik from '~/components/Admin/AiExperts/InstructionsMarkdownEditorFormik';
import CardDialog from '~/components/CardDialog';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import Text from '~/components/core/TextComponents/Text';
import ConfiguredFieldsSection from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/Sections/ConfiguredFieldsSection';
import { configuredFieldSchema } from '~/components/SystemConfiguration/FnolConfiguration/fnolConfigurationValidationSchema';
import { AiExpertScopeConfigurationInfoStructuredOutputComponent as StructuredOutputComponent } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import copies from './copies';
import { QA_EXPERT_KEY } from './scope';
import type { QaScopeFormValues, ScopeConfiguration, ScopeRow, SetAiExpertScopeRequest } from './types';

interface QaScopeEditorProps {
  scope: ScopeRow;
  /**
   * The scope's stored row, or `undefined` when nobody has configured it yet — an unconfigured
   * scope has no row rather than an off one. Passed in from the tab's single list read, so the
   * save echoes back exactly the row the table was rendered from.
   */
  configuration?: ScopeConfiguration;
  baseUrl: string;
  onClose: () => void;
  onSaved: () => void | Promise<unknown>;
}

// Same opinionation as Document Types. ConfiguredFieldsSection is an untyped .jsx whose excludeTypes
// prop infers as never[], so the cast mirrors the Document Types usage of the same component.
const EXCLUDED_FIELD_TYPES = ['location', 'contact'] as unknown as never[];

// Configured fields reuse the same opinionated validation as Document Types, but only while the
// expert is enabled for the scope. Disabling must always be persistable, so half-filled or
// duplicate-keyed rows (which are unused once disabled) must not block the save.
const validationSchema = Yup.object().shape({
  enabled: Yup.boolean(),
  instructions: Yup.string().nullable(),
  configured_fields: Yup.array().when('enabled', {
    is: true,
    then: Yup.array()
      .of(configuredFieldSchema({}))
      .test('configured-fields-unique-ids', 'Field keys must be unique', (value) =>
        isEmpty(value) ? true : uniqBy(value, 'id')?.length === value?.length
      ),
    otherwise: Yup.array(),
  }),
});

const QaScopeEditor: React.FC<QaScopeEditorProps> = ({ scope, configuration, baseUrl, onClose, onSaved }) => {
  const initialValues: QaScopeFormValues = {
    enabled: configuration?.standalone_enabled ?? false,
    instructions: configuration?.instructions ?? '',
    configured_fields: configuration?.configured_fields ?? [],
  };

  const handleSave = async (values: QaScopeFormValues) => {
    try {
      // The QA tab owns only the standalone switch, the instructions and the configured fields.
      // The PUT is a whole-row upsert, so the other fields are echoed back from the fetched row
      // rather than sent as a literal default: omitting them would wipe the threshold and the
      // render component of a row written elsewhere.
      const payload: SetAiExpertScopeRequest = {
        sub_organization_id: scope.sub_organization_id,
        lob: scope.lob,
        standalone_enabled: values.enabled,
        // The one field sent as a literal: the QA expert cannot run with the conductor and the
        // server refuses a row that turns it on, so echoing a stored `true` would have every save
        // here refused.
        conductor_enabled: false,
        // The one place the echoed fields are not independent: the threshold belongs to the
        // standalone run, so it has to drop to null in the same payload that turns standalone
        // off or the request is rejected.
        standalone_confidence_threshold: values.enabled
          ? (configuration?.standalone_confidence_threshold ?? null)
          : null,
        instructions: values.instructions || null,
        configured_fields: values.configured_fields,
        render_component: configuration?.render_component ?? StructuredOutputComponent.KeyValue,
      };
      await axios.put(`${baseUrl}/scopes/${QA_EXPERT_KEY}`, payload);
      // reloadData refetches the scope rows; it reports its own error and resolves to undefined
      // (never throws) on a failed refetch, leaving the tab in an error state. Only close once the
      // refresh actually returned data, so a failed refresh doesn't read as a failed save while the
      // scope table sits in an error state behind a closed dialog.
      const refreshed = await onSaved();
      if (refreshed !== undefined) {
        onClose();
      }
    } catch (error) {
      reportAxiosError(error);
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSave} enableReinitialize>
      {({ isSubmitting, handleSubmit }) => (
        <CardDialog
          isDialog
          open
          maxWidth="lg"
          fullWidth
          title={`${copies.tabTitle} — ${scope.sub_organization_name} / ${scope.lob}`}
          onClose={onClose}
          preventClose={isSubmitting}
          footerActions={
            <DialogFooterActions
              onClickPrimary={() => handleSubmit()}
              onClickSecondary={onClose}
              disabled={isSubmitting}
            />
          }
        >
          <div className="flex flex-col gap-20">
            <SwitchFormik id="enabled" label={copies.editor.enabledLabel} />
            <div className="flex flex-col gap-8">
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                {copies.editor.instructionsTitle}
              </Text>
              <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                {copies.editor.instructionsSubtitle}
              </Text>
              <InstructionsMarkdownEditorFormik id="instructions" />
            </div>
            <ConfiguredFieldsSection
              fieldId="configured_fields"
              hideCategories
              withoutRequired
              withoutRequiredForClaimClosing
              subtitle={copies.editor.configuredFieldsSubtitle}
              excludeTypes={EXCLUDED_FIELD_TYPES}
            />
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default QaScopeEditor;
